import { useCallback, useEffect, useMemo } from 'react';
import CategorySkeletonLoader from '../../category/CategorySkeletonLoader';
import queryString from 'query-string';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory, useRouteMatch } from 'react-router-dom';
import { useBreakpoint } from '@cat-ecom/pcc-components';
import {
  PAGE_BREAKPOINTS,
  STATUS,
  USER_TYPE_REGISTERED
} from '@app/constants/commonConstants';
import { useEspot, usePrevious } from '@app/hooks';
import useAnalytics from '@app/hooks/useAnalytics';
import {
  getPlpDetails,
  setPlpEquipmentChecked,
  setPLPProductCardExpanded
} from '@app/store/plp/actions';
import { isEmpty } from '@app/utils';
import { PLP_ESPOT_NAME } from '../constants';
import { CLP_PAGE_ESPOT_PLACEHOLDER_2 } from '../../top-category/constants';
import ProductListBody from '../ProductListBody';
import ProductListHeader from '../ProductListHeader';
import usePagination from '../usePagination';
import { loadPreferences } from '@app/store/shoppingPreferences/actions';
import { getLanguageDetails } from '../../../pages/account/shopping-preferences/utils/utils';
import ProductDisclaimers from '../../pdp/ProductDisclaimers';
import Conditional from '@app/components/common/Conditional';
import AddDealerFlyout from '../../alp/AssetLandingPage/AddDealerFlyout';

const ProductListPage = () => {
  const dispatch = useDispatch();
  const { dispatchWithImpressionEvent } = useAnalytics();
  const { setUrlToPageOneWithFilter } = usePagination();
  const { search, hash } = useLocation();
  const history = useHistory();
  const { url } = useRouteMatch();
  const taxDisclaimerMessageFooter = useSelector(
    s => s.dealerCustomer.taxDisclaimerMessageFooter
  );
  const plpDetailsStatus = useSelector(s => s.plpUI.getPlpDetailsStatus);
  const currentCategory = useSelector(s => s.plpUI.current);
  const isTopCategory = useSelector(
    s => s.categories.byId[currentCategory]?.isTopCategory
  );
  const paramTargeting = search ? `&${search.replace('?', '')}` : '';
  const espotNamesByCategoryLevel = isTopCategory
    ? `${CLP_PAGE_ESPOT_PLACEHOLDER_2}`
    : `${PLP_ESPOT_NAME}`;

  const espotNames = currentCategory ? espotNamesByCategoryLevel : '';
  const plpEspotParams = `&categoryId=${currentCategory}${paramTargeting}`;
  const { getEspotContent } = useEspot(espotNames, plpEspotParams);

  const initialEquipmentLoading = useSelector(
    s => s.myEquipment?.initialLoading
  );
  const uniqueIndex = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment?.uniqueIndex
  );
  const orderBy = useSelector(state => state.plpUI.orderBy);
  const filterByEquipment = useSelector(s => s.plpUI.filterByEquipment);
  const catalogEntries = useSelector(
    state => state.categories.byId[currentCategory]?.productsId
  );
  const didFirstPlpDetailsCallResolve = !!catalogEntries;
  const prevUniqueIndex = usePrevious(uniqueIndex);
  const parsed = queryString.parse(search);
  const notOnPageOne = +parsed.beginIndex > 0;
  const hasEntries = !isEmpty(catalogEntries);
  const resolvedWithoutEntries =
    !hasEntries && plpDetailsStatus === STATUS.RESOLVED;
  const pageDoesntExist = notOnPageOne && resolvedWithoutEntries;
  const getLanguageStatus = useSelector(s => s.footer?.getLanguageStatus);
  const supportedLanguages = useSelector(s => s.footer?.supportedLanguages);
  const userType = useSelector(s => s.common?.userType);
  const isSM = !useBreakpoint(PAGE_BREAKPOINTS.BLOCKS_LG);

  /* This block of code is needed to prevent the scrollRestoration for PLP,
  this due to there is a user story (US 245766) handling the scroll when 
  coming back from PDP and having this scroll restoration caused unexpected
  behavior

  For more information on this please refer to:
  https://developer.mozilla.org/en-US/docs/Web/API/History/scrollRestoration
  */
  if (window.history.scrollRestoration) {
    window.history.scrollRestoration = 'manual';
  }

  useEffect(() => {
    const currentUrl = window.location.href;
    const urlWithoutHash = currentUrl.replace(hash, '');
    const canonicalElement = document.querySelector('link[rel="canonical"]');
    if (canonicalElement) {
      canonicalElement.href = urlWithoutHash;
    }
  }, [hash, search]);

  useEffect(() => {
    if (pageDoesntExist) {
      dispatchWithImpressionEvent(getPlpDetails({ history, url, hash }));
    }
  }, [
    dispatch,
    dispatchWithImpressionEvent,
    hash,
    history,
    pageDoesntExist,
    url
  ]);
  // this useEffect handles the first plp details call only
  useEffect(() => {
    if (
      !initialEquipmentLoading &&
      plpDetailsStatus === STATUS.IDLE &&
      currentCategory
    ) {
      const params = {};
      if (notOnPageOne) {
        const pageNumber = Math.ceil(+parsed.beginIndex / parsed.pageSize);
        params.offset = pageNumber;
      }
      dispatchWithImpressionEvent(
        getPlpDetails({ ...params, hash, history, search, url })
      );
    }
  }, [
    dispatchWithImpressionEvent,
    hash,
    history,
    initialEquipmentLoading,
    notOnPageOne,
    parsed.beginIndex,
    parsed.pageSize,
    plpDetailsStatus,
    search,
    url,
    currentCategory
  ]);

  const languageOptions = useMemo(
    () =>
      getLanguageStatus === STATUS.RESOLVED
        ? getLanguageDetails(supportedLanguages)
        : [],
    [getLanguageStatus, supportedLanguages]
  );

  useEffect(() => {
    const { scrollTo } = queryString.parse(window.location.search);
    getLanguageStatus === STATUS.RESOLVED &&
      userType === USER_TYPE_REGISTERED &&
      dispatch(loadPreferences({ scrollTo, languages: languageOptions }));
  }, [dispatch, getLanguageStatus, languageOptions, userType]);

  const equipmentChanged = !!(
    !initialEquipmentLoading &&
    plpDetailsStatus !== STATUS.IDLE &&
    uniqueIndex !== prevUniqueIndex
  );
  const handleEquipmentCheck = useCallback(
    filterByEquipment => {
      setUrlToPageOneWithFilter({ filterByEquipment });
      dispatch(setPLPProductCardExpanded(null));
      dispatchWithImpressionEvent(setPlpEquipmentChecked(filterByEquipment));
    },
    [dispatch, dispatchWithImpressionEvent, setUrlToPageOneWithFilter]
  );

  // this use effect will trigger the redraw if equipment changes
  useEffect(() => {
    if (equipmentChanged) {
      // when selecting equipment when none is selected we mimic checking the check to update redux, url and fetch
      if (!prevUniqueIndex) {
        handleEquipmentCheck(true);
        // when selecting different equipment or deselecting
      } else {
        // update the url to remove filterByEquipment param when deselecting equipment
        if (!uniqueIndex) {
          setUrlToPageOneWithFilter({
            filterByEquipment: undefined,
            orderBy
          });
        }
        // fatch if the checkbox was checked
        if (filterByEquipment) {
          dispatchWithImpressionEvent(
            getPlpDetails({}, { facetsLoading: true })
          );
        }
      }
    }
  }, [
    dispatch,
    filterByEquipment,
    equipmentChanged,
    dispatchWithImpressionEvent,
    hash,
    history,
    search,
    url,
    uniqueIndex,
    setUrlToPageOneWithFilter,
    orderBy,
    prevUniqueIndex,
    handleEquipmentCheck
  ]);

  if (
    !didFirstPlpDetailsCallResolve &&
    (plpDetailsStatus === STATUS.PENDING || plpDetailsStatus === STATUS.IDLE)
  ) {
    return <CategorySkeletonLoader />;
  }

  const productListBodyEspot = isTopCategory
    ? getEspotContent(CLP_PAGE_ESPOT_PLACEHOLDER_2)
    : getEspotContent(PLP_ESPOT_NAME);

  return (
    <main data-testid="plpcontainer" className="container bg-white">
      <ProductListHeader />
      <ProductListBody
        handleEquipmentCheck={handleEquipmentCheck}
        pageDoesntExist={pageDoesntExist}
        espotData={productListBodyEspot}
      />
      {!!taxDisclaimerMessageFooter && (
        <ProductDisclaimers
          taxDisclaimerMessageFooter={taxDisclaimerMessageFooter}
          className="py-3"
          disclaimerClassName="font-italic"
        />
      )}
      <Conditional test={isSM}>
        <AddDealerFlyout />
      </Conditional>
    </main>
  );
};

export default ProductListPage;
